// Case Status Tab - Constants & Configuration
// Phase definitions, milestone configs, color mappings

import {
  FileText,
  Shield,
  Mail,
  Scale,
  Trophy,
  Calendar,
  Gavel,
  CheckCircle,
  Clock,
  Minus,
  Plus,
  MessageSquare,
  Banknote,
  Archive,
  type LucideIcon,
} from 'lucide-react'

// ============================================================================
// Phase Types & Definitions
// ============================================================================

export type CasePhase =
  | 'intake'
  | 'rsv'
  | 'pre_litigation'
  | 'litigation'
  | 'resolution'
  | 'closed'

export type MilestoneStatus = 'completed' | 'current' | 'pending' | 'skipped'

export interface PhaseConfig {
  id: CasePhase
  label: string
  shortLabel: string
  gradient: string
  accentColor: string
  icon: LucideIcon
  description: string
}

export interface MilestoneConfig {
  id: string
  label: string
  icon: LucideIcon
  triggerActions: string[] // CaseAction types that mark this as completed
  phase: CasePhase
  conditional?: boolean // Only show if relevant actions exist
}

// ============================================================================
// Phase Configuration
// ============================================================================

export const phaseConfig: Record<CasePhase, PhaseConfig> = {
  intake: {
    id: 'intake',
    label: 'Eingang',
    shortLabel: 'Eingang',
    gradient: 'from-slate-50 to-blue-50/30',
    accentColor: 'blue',
    icon: FileText,
    description: 'Fall wurde eingereicht und wird geprüft',
  },
  rsv: {
    id: 'rsv',
    label: 'RSV-Prüfung',
    shortLabel: 'RSV',
    gradient: 'from-slate-50 to-amber-50/30',
    accentColor: 'amber',
    icon: Shield,
    description: 'Deckungsanfrage bei Rechtsschutzversicherung',
  },
  pre_litigation: {
    id: 'pre_litigation',
    label: 'Vorgerichtlich',
    shortLabel: 'Vorger.',
    gradient: 'from-slate-50 to-orange-50/30',
    accentColor: 'orange',
    icon: Mail,
    description: 'Außergerichtliche Geltendmachung',
  },
  litigation: {
    id: 'litigation',
    label: 'Gerichtsverfahren',
    shortLabel: 'Gericht',
    gradient: 'from-slate-50 to-purple-50/30',
    accentColor: 'purple',
    icon: Scale,
    description: 'Klage eingereicht, Verfahren läuft',
  },
  resolution: {
    id: 'resolution',
    label: 'Abschluss',
    shortLabel: 'Abschluss',
    gradient: 'from-slate-50 to-emerald-50/30',
    accentColor: 'emerald',
    icon: Trophy,
    description: 'Fall abgeschlossen',
  },
  closed: {
    id: 'closed',
    label: 'Abgeschlossen',
    shortLabel: 'Geschl.',
    gradient: 'from-slate-100 to-slate-200/50',
    accentColor: 'slate',
    icon: CheckCircle,
    description: 'Fall archiviert',
  },
}

// Order of phases for progress display
export const phaseOrder: CasePhase[] = [
  'intake',
  'rsv',
  'pre_litigation',
  'litigation',
  'resolution',
  'closed',
]

// ============================================================================
// Milestone Configuration
// ============================================================================

export const milestoneConfig: MilestoneConfig[] = [
  {
    id: 'case_submitted',
    label: 'Fall eingereicht',
    icon: FileText,
    triggerActions: [], // Always completed for any case
    phase: 'intake',
  },
  {
    id: 'rsv_coverage',
    label: 'RSV Deckung',
    icon: Shield,
    triggerActions: ['rsv_approved', 'rsv_denied', 'rsv_partial'],
    phase: 'rsv',
  },
  {
    id: 'mahnschreiben',
    label: 'Mahnschreiben',
    icon: Mail,
    triggerActions: ['mahnschreiben_sent'],
    phase: 'pre_litigation',
  },
  {
    id: 'klage',
    label: 'Klage eingereicht',
    icon: Scale,
    triggerActions: ['klage_filed'],
    phase: 'litigation',
  },
  {
    id: 'hearing',
    label: 'Gerichtstermin',
    icon: Calendar,
    triggerActions: ['hearing_scheduled', 'hearing_held'],
    phase: 'litigation',
    conditional: true,
  },
  {
    id: 'judgment',
    label: 'Urteil',
    icon: Gavel,
    triggerActions: ['judgment'],
    phase: 'litigation',
    conditional: true,
  },
  {
    id: 'resolution',
    label: 'Abschluss',
    icon: Trophy,
    triggerActions: ['case_won', 'case_lost', 'settlement_reached', 'payment_received', 'case_closed'],
    phase: 'resolution',
  },
]

// ============================================================================
// Phase Inference Logic
// ============================================================================

// Action types that trigger each phase (checked in reverse priority order)
const phaseActionMap: Record<CasePhase, string[]> = {
  closed: ['case_closed'],
  resolution: ['case_won', 'case_lost', 'settlement_reached', 'payment_received'],
  litigation: ['klage_filed', 'klage_served', 'hearing_scheduled', 'hearing_held', 'judgment'],
  pre_litigation: ['mahnschreiben_sent', 'defendant_response', 'defendant_rejected', 'defendant_offer'],
  rsv: ['rsv_request_sent'],
  intake: [], // Default phase
}

export function inferCasePhase(actionTypes: Set<string>): CasePhase {
  // Check phases from most advanced to least
  for (const phase of ['closed', 'resolution', 'litigation', 'pre_litigation', 'rsv'] as CasePhase[]) {
    const triggers = phaseActionMap[phase]
    if (triggers.some((action) => actionTypes.has(action))) {
      return phase
    }
  }
  return 'intake'
}

// ============================================================================
// Milestone Status Inference
// ============================================================================

export interface MilestoneWithStatus extends MilestoneConfig {
  status: MilestoneStatus
  date?: string
  detail?: string
}

export function deriveMilestoneStatuses(
  actionTypes: Set<string>,
  actions: Array<{ action_type: string; action_date: string; title?: string }>,
  currentPhase: CasePhase,
  hasRsvRequest: boolean
): MilestoneWithStatus[] {
  const milestones: MilestoneWithStatus[] = []
  const currentPhaseIndex = phaseOrder.indexOf(currentPhase)

  for (const config of milestoneConfig) {
    // Skip conditional milestones unless they have relevant actions
    if (config.conditional) {
      const hasRelevantAction = config.triggerActions.some((a) => actionTypes.has(a))
      if (!hasRelevantAction) continue
    }

    // Special case: RSV milestone - skip if no RSV request was sent and we're past RSV phase
    if (config.id === 'rsv_coverage' && !hasRsvRequest && currentPhaseIndex > phaseOrder.indexOf('rsv')) {
      milestones.push({
        ...config,
        status: 'skipped',
        detail: 'Übersprungen',
      })
      continue
    }

    // Find the action that triggered this milestone
    const triggeringAction = actions.find((a) => config.triggerActions.includes(a.action_type))
    const milestonePhaseIndex = phaseOrder.indexOf(config.phase)

    let status: MilestoneStatus
    let date: string | undefined
    let detail: string | undefined

    if (config.id === 'case_submitted') {
      // Always completed
      status = 'completed'
      date = actions.length > 0 ? actions[actions.length - 1]?.action_date : undefined
    } else if (triggeringAction) {
      status = 'completed'
      date = triggeringAction.action_date
      detail = triggeringAction.title
    } else if (milestonePhaseIndex < currentPhaseIndex) {
      // Phase was passed without this milestone - it was skipped
      status = 'skipped'
    } else if (milestonePhaseIndex === currentPhaseIndex) {
      // This is the current phase
      status = 'current'
    } else {
      // Future milestone
      status = 'pending'
    }

    milestones.push({
      ...config,
      status,
      date,
      detail,
    })
  }

  return milestones
}

// ============================================================================
// Animation Configuration
// ============================================================================

export const springConfig = {
  default: { type: 'spring' as const, stiffness: 300, damping: 30 },
  bouncy: { type: 'spring' as const, stiffness: 400, damping: 20 },
  gentle: { type: 'spring' as const, stiffness: 200, damping: 25 },
}

export const animationVariants = {
  cardEnter: {
    hidden: { opacity: 0, y: 20, scale: 0.98 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: springConfig.default,
    },
  },
  staggerContainer: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  },
  milestoneNode: {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: springConfig.default,
    },
  },
  progressLine: {
    hidden: { scaleY: 0, originY: 0 },
    visible: {
      scaleY: 1,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  },
}

// ============================================================================
// Status Icon & Color Mapping
// ============================================================================

export const statusConfig: Record<MilestoneStatus, { icon: LucideIcon; colorClass: string; bgClass: string }> = {
  completed: {
    icon: CheckCircle,
    colorClass: 'text-emerald-500',
    bgClass: 'bg-emerald-100',
  },
  current: {
    icon: Clock,
    colorClass: 'text-blue-500',
    bgClass: 'bg-blue-100',
  },
  pending: {
    icon: Clock,
    colorClass: 'text-slate-300',
    bgClass: 'bg-slate-100',
  },
  skipped: {
    icon: Minus,
    colorClass: 'text-slate-400',
    bgClass: 'bg-slate-100',
  },
}

// ============================================================================
// Accent Color Classes
// ============================================================================

export const accentColorClasses: Record<string, {
  bg: string
  bgLight: string
  text: string
  border: string
  ring: string
}> = {
  blue: {
    bg: 'bg-blue-500',
    bgLight: 'bg-blue-50',
    text: 'text-blue-600',
    border: 'border-blue-200',
    ring: 'ring-blue-500/20',
  },
  amber: {
    bg: 'bg-amber-500',
    bgLight: 'bg-amber-50',
    text: 'text-amber-600',
    border: 'border-amber-200',
    ring: 'ring-amber-500/20',
  },
  orange: {
    bg: 'bg-orange-500',
    bgLight: 'bg-orange-50',
    text: 'text-orange-600',
    border: 'border-orange-200',
    ring: 'ring-orange-500/20',
  },
  purple: {
    bg: 'bg-purple-500',
    bgLight: 'bg-purple-50',
    text: 'text-purple-600',
    border: 'border-purple-200',
    ring: 'ring-purple-500/20',
  },
  emerald: {
    bg: 'bg-emerald-500',
    bgLight: 'bg-emerald-50',
    text: 'text-emerald-600',
    border: 'border-emerald-200',
    ring: 'ring-emerald-500/20',
  },
  slate: {
    bg: 'bg-slate-500',
    bgLight: 'bg-slate-50',
    text: 'text-slate-600',
    border: 'border-slate-200',
    ring: 'ring-slate-500/20',
  },
}

// ============================================================================
// Next Steps Configuration
// ============================================================================

export type NextStepActionType =
  | 'generate_mahnschreiben'
  | 'generate_rsv_request'
  | 'generate_klage'
  | 'record_rsv_response'
  | 'record_defendant_response'
  | 'record_hearing'
  | 'record_payment'
  | 'close_case'
  | 'add_action'

export interface NextStepConfig {
  id: NextStepActionType
  label: string
  description?: string
  icon: LucideIcon
  actionType: 'generate_document' | 'open_modal'
  // For generate_document actions
  documentType?: string
  // For open_modal actions
  preselectedActionType?: string
}

export const nextStepConfigs: Record<NextStepActionType, NextStepConfig> = {
  generate_mahnschreiben: {
    id: 'generate_mahnschreiben',
    label: 'Mahnschreiben',
    description: 'Außergerichtliche Forderung',
    icon: Mail,
    actionType: 'generate_document',
    documentType: 'mahnschreiben',
  },
  generate_rsv_request: {
    id: 'generate_rsv_request',
    label: 'RSV anfragen',
    description: 'Deckungszusage anfragen',
    icon: Shield,
    actionType: 'generate_document',
    documentType: 'rsv_request',
  },
  generate_klage: {
    id: 'generate_klage',
    label: 'Klage erstellen',
    description: 'Gerichtliche Geltendmachung',
    icon: Scale,
    actionType: 'generate_document',
    documentType: 'klage',
  },
  record_rsv_response: {
    id: 'record_rsv_response',
    label: 'RSV-Antwort',
    description: 'Deckungszusage eintragen',
    icon: Shield,
    actionType: 'open_modal',
    preselectedActionType: 'rsv_approved',
  },
  record_defendant_response: {
    id: 'record_defendant_response',
    label: 'Antwort eintragen',
    description: 'Reaktion der Airline',
    icon: MessageSquare,
    actionType: 'open_modal',
    preselectedActionType: 'defendant_response',
  },
  record_hearing: {
    id: 'record_hearing',
    label: 'Termin eintragen',
    description: 'Gerichtstermin erfassen',
    icon: Calendar,
    actionType: 'open_modal',
    preselectedActionType: 'hearing_scheduled',
  },
  record_payment: {
    id: 'record_payment',
    label: 'Zahlung eintragen',
    description: 'Zahlungseingang erfassen',
    icon: Banknote,
    actionType: 'open_modal',
    preselectedActionType: 'payment_received',
  },
  close_case: {
    id: 'close_case',
    label: 'Fall abschließen',
    description: 'Fall archivieren',
    icon: Archive,
    actionType: 'open_modal',
    preselectedActionType: 'case_closed',
  },
  add_action: {
    id: 'add_action',
    label: 'Aktion hinzufügen',
    description: 'Beliebige Aktion',
    icon: Plus,
    actionType: 'open_modal',
  },
}

export interface NextStep extends NextStepConfig {
  priority: 'primary' | 'secondary'
}

/**
 * Derive contextual next steps based on current phase and completed actions
 */
export function getNextSteps(
  phase: CasePhase,
  actionTypes: Set<string>
): NextStep[] {
  const steps: NextStep[] = []

  // Check RSV status
  const hasRsvRequest = actionTypes.has('rsv_request_sent')
  const hasRsvResolved = actionTypes.has('rsv_approved') || actionTypes.has('rsv_denied') || actionTypes.has('rsv_partial')
  const hasMahnschreiben = actionTypes.has('mahnschreiben_sent')
  const hasDefendantResponse = actionTypes.has('defendant_response') || actionTypes.has('defendant_rejected') || actionTypes.has('defendant_offer')

  switch (phase) {
    case 'intake':
      // New case - suggest RSV or go straight to Mahnschreiben
      steps.push({ ...nextStepConfigs.generate_rsv_request, priority: 'primary' })
      steps.push({ ...nextStepConfigs.generate_mahnschreiben, priority: 'secondary' })
      steps.push({ ...nextStepConfigs.add_action, priority: 'secondary' })
      break

    case 'rsv':
      if (!hasRsvResolved) {
        // Waiting for RSV response
        steps.push({ ...nextStepConfigs.record_rsv_response, priority: 'primary' })
        steps.push({ ...nextStepConfigs.generate_mahnschreiben, priority: 'secondary' })
      } else {
        // RSV resolved, move to Mahnschreiben
        steps.push({ ...nextStepConfigs.generate_mahnschreiben, priority: 'primary' })
      }
      steps.push({ ...nextStepConfigs.add_action, priority: 'secondary' })
      break

    case 'pre_litigation':
      if (!hasDefendantResponse) {
        // Waiting for airline response
        steps.push({ ...nextStepConfigs.record_defendant_response, priority: 'primary' })
        steps.push({ ...nextStepConfigs.generate_klage, priority: 'secondary' })
      } else {
        // Defendant responded, suggest Klage
        steps.push({ ...nextStepConfigs.generate_klage, priority: 'primary' })
      }
      steps.push({ ...nextStepConfigs.add_action, priority: 'secondary' })
      break

    case 'litigation':
      // In litigation - flexible actions
      steps.push({ ...nextStepConfigs.record_hearing, priority: 'primary' })
      steps.push({ ...nextStepConfigs.record_payment, priority: 'secondary' })
      steps.push({ ...nextStepConfigs.add_action, priority: 'secondary' })
      break

    case 'resolution':
      // Case is resolving
      steps.push({ ...nextStepConfigs.record_payment, priority: 'primary' })
      steps.push({ ...nextStepConfigs.close_case, priority: 'secondary' })
      steps.push({ ...nextStepConfigs.add_action, priority: 'secondary' })
      break

    case 'closed':
      // Case is closed - minimal actions
      steps.push({ ...nextStepConfigs.add_action, priority: 'secondary' })
      break
  }

  // Return max 3 steps
  return steps.slice(0, 3)
}

// ============================================================================
// Compact Milestone Config (for horizontal display)
// ============================================================================

export interface CompactMilestone {
  id: string
  label: string
  shortLabel: string
}

export const compactMilestones: CompactMilestone[] = [
  { id: 'rsv', label: 'RSV Deckung', shortLabel: 'RSV' },
  { id: 'mahnschreiben', label: 'Mahnschreiben', shortLabel: 'Mahn' },
  { id: 'klage', label: 'Klage', shortLabel: 'Klage' },
  { id: 'resolution', label: 'Abschluss', shortLabel: 'Abschl.' },
]
